// Who may do what (18-channel-settings.md, "Who may do what", "Roles"): the power levels with
// Matrix's defaults, the founders of room version 12 and newer, outranking, and the fixed roles
// Admin (100), Moderator (50) and Member (0) — the role editor and free roles were candidates to drop.
import { EventType, type Room } from 'matrix-js-sdk';
import { t } from '../i18n';

export interface PowerLevels {
  users?: Record<string, number>;
  users_default?: number;
  events?: Record<string, number>;
  events_default?: number;
  state_default?: number;
  invite?: number;
  redact?: number;
  kick?: number;
  ban?: number;
  notifications?: { room?: number };
}

export const levels = (room: Room): PowerLevels => room.currentState.getStateEvents(EventType.RoomPowerLevels, '')?.getContent() ?? {};

/** The founders: the creator and additional creators, in room version 12 or newer. */
export function founders(room: Room): Set<string> {
  if ((Number(room.getVersion()) || 0) < 12) return new Set();
  const create = room.currentState.getStateEvents(EventType.RoomCreate, '');
  return new Set([create?.getSender() ?? '', ...((create?.getContent().additional_creators as string[] | undefined) ?? [])].filter(Boolean));
}

/** A person's power (founders: above everyone). */
export const powerOf = (room: Room, userId: string) => (founders(room).has(userId) ? Infinity : (levels(room).users?.[userId] ?? levels(room).users_default ?? 0));

/** A founder outranks everyone but founders; otherwise strictly more power. */
export const outranks = (room: Room, a: string, b: string) => {
  const [pa, pb] = [powerOf(room, a), powerOf(room, b)];
  return pa === Infinity ? pb !== Infinity : pa > pb;
};

/** The role's name for a power: Founder, Admin, Moderator, Member, Muted; others after the next lower one. */
export function roleName(power: number): string {
  const named: [number, string][] = [
    [100, t('roles.admin')],
    [50, t('roles.moderator')],
    [0, t('roles.member')],
    [-1, t('roles.muted')],
  ];
  if (power === Infinity) return t('roles.founder');
  const exact = named.find(([p]) => p === power);
  if (exact) return exact[1];
  const lower = named.find(([p]) => p < power);
  return lower ? `${lower[1]} ${power}` : t('roles.level', { n: power });
}

/** The fixed roles a person can be given, highest first. */
export const ROLES = [100, 50, 0];

/** The fixed role a power stands for, for the permission rows (at or below 0, 50, 100). */
export const roleFor = (power: number) => (power <= 0 ? 0 : power <= 50 ? 50 : 100);
